import { DeleteForever } from '@mui/icons-material'
import { Button, Card, CardContent, Grid, Typography } from '@mui/material'
import { DeleteButton, EditButton, GreenText, LabelField, LockButton, LockedChip, Title } from 'components'
import { useLayout } from 'context'
import { generateFedExLabels, getDeliveryTypes, getPackageTypes, getUSStateCodes } from 'controllers/fedEx'
import {
  createOhioBuildABox,
  createOhioBuildABoxBundle,
  deleteOhioBuildABox,
  deleteOhioBuildABoxBundle,
  generateCombinedOhioCoverLetter,
  generateOhioCoverLetter,
  getOhioBuildABox,
  lockOhioBuildABox,
  lockOhioBuildABoxBundle,
  manageOhioBuildABoxBundleVehicles,
  unlockOhioBuildABox,
  unlockOhioBuildABoxBundle,
  updateFedExOutboundLabel,
  updateFedExReturnLabel,
  updateOhioBuildABoxBundle
} from 'controllers/ohioBuildABoxes'
import { FedExDeliveryTypeDto, FedExPackageTypeDto, FedExShipmentDto, fedExShipmentInit, FedExUSStateCodeDto } from 'dtos/fedEx'
import { OhioBuildABoxBundleDto, OhioBuildABoxDto } from 'dtos/ohioBuildABoxes'
import { enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import AddEditBundleDialog from './AddEditBundleDialog'
import FedExLabelCard from './FedExLabelCard'
import ManageVehiclesDialog from './ManageVehiclesDialog'
import OhioBuildABoxDetailsCard from './OhioBuildABoxDetailsCard'
import FedExLabelDialog from './FedExLabelDialog'

const ohioBuildABoxBundleInit: OhioBuildABoxBundleDto = {
  ohioTitleOwnerName: '',
  vehicles: []
}

const getFedExLabelShipmentGuids = (ohioBuildABox?: OhioBuildABoxDto): string[] => {
  const shipmentGuids: string[] = []
  const outboundShipmentGuid = ohioBuildABox?.fedExOutboundShipment?.fedExShipmentGuid
  const returnShipmentGuid = ohioBuildABox?.fedExReturnShipment?.fedExShipmentGuid

  if (outboundShipmentGuid) shipmentGuids.push(outboundShipmentGuid)
  if (returnShipmentGuid) shipmentGuids.push(returnShipmentGuid)

  return shipmentGuids
}

export default function MichiganOutgoingTitlesDetails() {
  const { ohioBuildABoxGuid } = useParams()
  const { setIsLayoutLoading } = useLayout()
  const navigate = useNavigate()

  const [values, setValues] = useState<OhioBuildABoxDto>()

  // Bundle Dialog State
  const [bundleInit, setBundleInit] = useState<OhioBuildABoxBundleDto>(ohioBuildABoxBundleInit)
  // Add/Edit Bundle Dialog
  const [isBundleDialogOpen, setIsBundleDialogOpen] = useState<boolean>(false)
  const [isBundleSaving, setIsBundleSaving] = useState<boolean>(false)
  // Manage Vehicles Dialog
  const [isVehiclesDialogOpen, setIsVehiclesDialogOpen] = useState<boolean>(false)
  const [isVehiclesSaving, setIsVehiclesSaving] = useState<boolean>(false)

  const [deliveryTypes, setDeliveryTypes] = useState<FedExDeliveryTypeDto[]>([])
  const [packageTypes, setPackageTypes] = useState<FedExPackageTypeDto[]>([])
  const [usStateCodes, setUSStateCodes] = useState<FedExUSStateCodeDto[]>([])

  const [isFedExDialogOpen, setIsFedExDialogOpen] = useState<'outbound' | 'return' | undefined>()
  const [fedExInit, setFedExInit] = useState<FedExShipmentDto>(fedExShipmentInit)
  const [isFedExSaving, setIsFedExSaving] = useState<boolean>(false)

  useEffect(() => {
    getDeliveryTypes().then(res => setDeliveryTypes(res.value))
    getPackageTypes().then(res => setPackageTypes(res.value))
    getUSStateCodes().then(res => setUSStateCodes(res.value))
  }, [])

  useEffect(() => {
    if (!ohioBuildABoxGuid) return

    //
    const isNew: boolean = ohioBuildABoxGuid.trim().toUpperCase() === 'NEW'

    setIsLayoutLoading(true)
    if (isNew) {
      createOhioBuildABox()
        .then(res => {
          enqueueSnackbar('Michigan Outgoing Title Created Successfully!', { variant: 'success' })
          navigate(`/titles/ohio-build-a-boxes/${encodeURIComponent(res.value.ohioBuildABoxGuid!)}`, { replace: true })
        })
        .catch(() => navigate(-1))
    } else {
      getOhioBuildABox(ohioBuildABoxGuid)
        .then(res => setValues(res.value))
        .finally(() => setIsLayoutLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ohioBuildABoxGuid])

  const fedExLabelShipmentGuids = getFedExLabelShipmentGuids(values)

  return (
    <>
      <Title>Build A Box (Ohio) Details</Title>

      <AddEditBundleDialog
        initialValues={bundleInit}
        isLoading={isBundleSaving}
        onClose={() => setIsBundleDialogOpen(false)}
        onSave={bundle => {
          if (!values?.ohioBuildABoxGuid) return

          setIsBundleSaving(true)
          if (bundle.ohioBuildABoxBundleGuid) {
            updateOhioBuildABoxBundle(bundle)
              .then(res => {
                enqueueSnackbar('Bundle Saved Successfully!', { variant: 'success' })
                setValues({
                  ...values,
                  ohioBuildABoxBundles: values.ohioBuildABoxBundles.map(obabb =>
                    obabb.ohioBuildABoxBundleGuid === bundle.ohioBuildABoxBundleGuid ? bundle : obabb
                  )
                })
                setIsBundleDialogOpen(false)
              })
              .finally(() => setIsBundleSaving(false))
          } else {
            createOhioBuildABoxBundle(values?.ohioBuildABoxGuid!, bundle)
              .then(res => {
                enqueueSnackbar('Bundle Saved Successfully!', { variant: 'success' })
                setValues({ ...values, ohioBuildABoxBundles: [...values.ohioBuildABoxBundles, res.value] })
                setIsBundleDialogOpen(false)
              })
              .finally(() => setIsBundleSaving(false))
          }
        }}
        open={isBundleDialogOpen}
      />

      <ManageVehiclesDialog
        initialValues={bundleInit}
        isLoading={isVehiclesSaving}
        onClose={() => setIsVehiclesDialogOpen(false)}
        onSave={vehicles => {
          if (!bundleInit.ohioBuildABoxBundleGuid) return
          setIsVehiclesSaving(true)
          manageOhioBuildABoxBundleVehicles(bundleInit.ohioBuildABoxBundleGuid, vehicles)
            .then(res => {
              enqueueSnackbar('Vehicles Saved Successfully!', { variant: 'success' })
              setValues({
                ...values,
                ohioBuildABoxBundles: values!.ohioBuildABoxBundles.map(obabb =>
                  obabb.ohioBuildABoxBundleGuid === bundleInit.ohioBuildABoxBundleGuid
                    ? {
                        ...obabb,
                        vehicles: res.value
                      }
                    : obabb
                )
              })
              setIsVehiclesDialogOpen(false)
            })
            .finally(() => setIsVehiclesSaving(false))
        }}
        open={isVehiclesDialogOpen}
      />

      <FedExLabelDialog
        deliveryTypes={deliveryTypes}
        initialValues={fedExInit}
        isLoading={isFedExSaving}
        onClose={() => setIsFedExDialogOpen(undefined)}
        onSave={fedExShipment => {
          if (!values?.ohioBuildABoxGuid || !isFedExDialogOpen) return

          setIsFedExSaving(true)
          if (isFedExDialogOpen === 'outbound') {
            updateFedExOutboundLabel(values.ohioBuildABoxGuid, fedExShipment)
              .then(res => {
                enqueueSnackbar('FedEx Outbound Label Saved Successfully!', { variant: 'success' })
                setValues({ ...values, fedExOutboundShipment: res.value })
                setIsFedExDialogOpen(undefined)
              })
              .finally(() => setIsFedExSaving(false))
          } else if (isFedExDialogOpen === 'return') {
            updateFedExReturnLabel(values.ohioBuildABoxGuid, fedExShipment)
              .then(res => {
                enqueueSnackbar('FedEx Return Label Saved Successfully!', { variant: 'success' })
                setValues({ ...values, fedExReturnShipment: res.value })
                setIsFedExDialogOpen(undefined)
              })
              .finally(() => setIsFedExSaving(false))
          }
        }}
        open={Boolean(isFedExDialogOpen)}
        packageTypes={packageTypes}
        title={isFedExDialogOpen === 'return' ? 'FedEx Return Label Management' : 'FedEx Outbound Label Management'}
        usStateCodes={usStateCodes}
      />

      <Grid container spacing={1} alignItems='flex-start'>
        <Grid item xs={12}>
          <OhioBuildABoxDetailsCard values={values}>
            <Grid item>
              <LockButton
                disabled={values && (values.ohioBuildABoxBundles.length === 0 || values.ohioBuildABoxBundles.some(obabb => !obabb.isLocked))}
                isLocked={values?.isLocked}
                onLock={() => {
                  if (!values?.ohioBuildABoxGuid) return
                  setIsLayoutLoading(true)
                  lockOhioBuildABox(values.ohioBuildABoxGuid)
                    .then(res => {
                      enqueueSnackbar('Build A Box Locked Successfully!', { variant: 'success' })
                      setValues(res.value)
                    })
                    .finally(() => setIsLayoutLoading(false))
                }}
                onUnlock={() => {
                  if (!values?.ohioBuildABoxGuid) return

                  if (
                    window.confirm(
                      'Are you sure you wish to unlock this box? Unlocking the box will delete the Tracking #s from both the FedEx Outbound Label and FedEx Return Label and then VTS2 will deduct the prorated amount for each label/shipment from all vehicles in the box. This cannot be undone.'
                    )
                  ) {
                    setIsLayoutLoading(true)
                    unlockOhioBuildABox(values.ohioBuildABoxGuid)
                      .then(res => {
                        enqueueSnackbar('Build A Box Unlocked Successfully!', { variant: 'success' })
                        setValues(res.value)
                      })
                      .finally(() => setIsLayoutLoading(false))
                  }
                }}
              />
            </Grid>

            <Grid item>
              <Button
                color='error'
                disabled={!values?.ohioBuildABoxGuid || values?.isLocked}
                endIcon={<DeleteForever />}
                onClick={() => {
                  if (values?.ohioBuildABoxGuid && window.confirm('Are you sure you would like to delete this Build A Box? This cannot be undone.')) {
                    setIsLayoutLoading(true)
                    deleteOhioBuildABox(values.ohioBuildABoxGuid).then(() => {
                      enqueueSnackbar('Build A Box Deleted Successfully!', { variant: 'success' })
                      navigate('/titles/ohio-build-a-boxes', { replace: true })
                    })
                  }
                }}
                variant='text'
              >
                DELETE
              </Button>
            </Grid>

            <Grid item>
              <Button
                color='primary'
                disabled={!values?.isLocked}
                onClick={() => {
                  if (values?.ohioBuildABoxGuid) {
                    setIsLayoutLoading(true)
                    generateCombinedOhioCoverLetter(values.ohioBuildABoxGuid).finally(() => setIsLayoutLoading(false))
                  }
                }}
                size='small'
                variant='contained'
              >
                PRINT OHIO TITLES COVER LETTER FOR ALL LOCKED BUNDLES
              </Button>
            </Grid>

            <Grid item>
              <Button
                color='primary'
                disabled={!values?.isLocked || fedExLabelShipmentGuids.length === 0}
                onClick={() => {
                  if (fedExLabelShipmentGuids.length > 0) {
                    setIsLayoutLoading(true)
                    generateFedExLabels(fedExLabelShipmentGuids).finally(() => setIsLayoutLoading(false))
                  }
                }}
                size='small'
                variant='contained'
              >
                PRINT FEDEX LABELS
              </Button>
            </Grid>

            <Grid item>
              <Button
                color='primary'
                disabled={values?.isLocked}
                onClick={() => {
                  setBundleInit(ohioBuildABoxBundleInit)
                  setIsBundleDialogOpen(true)
                }}
                size='small'
                variant='contained'
              >
                ADD BUNDLE
              </Button>
            </Grid>
          </OhioBuildABoxDetailsCard>
        </Grid>

        <Grid item container xs={12} alignItems='flex-start'>
          <Grid item xs={7} container spacing={1} alignItems='flex-start'>
            {values?.ohioBuildABoxBundles.map((ohioBuildABoxBundle, index) => (
              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Grid container spacing={2} alignItems='flex-start'>
                      <Grid item xs={12} container alignItems='center'>
                        <Grid item>
                          <Typography fontWeight='bold' variant='h6'>
                            Bundle {index + 1}
                          </Typography>
                        </Grid>

                        <Grid item>
                          <LockedChip isLocked={ohioBuildABoxBundle.isLocked} />
                        </Grid>
                      </Grid>

                      <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
                        <Grid item xs={12} sm='auto'>
                          <LabelField label='Vehicles In Bundle' value={ohioBuildABoxBundle.vehicles.length} />
                        </Grid>

                        <Grid item xs={12} sm='auto'>
                          <EditButton
                            onEdit={() => {
                              setBundleInit(ohioBuildABoxBundle)
                              setIsVehiclesDialogOpen(true)
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Grid item xs={12} container alignItems='center' justifyContent='space-between'>
                        <Grid item xs={12} sm='auto'>
                          <LabelField label='Owner' value={ohioBuildABoxBundle.ohioTitleOwnerName} />
                        </Grid>

                        <Grid item xs={12} sm='auto'>
                          <EditButton
                            disabled={ohioBuildABoxBundle.vehicles.length > 0}
                            onEdit={() => {
                              setBundleInit(ohioBuildABoxBundle)
                              setIsBundleDialogOpen(true)
                            }}
                          />
                        </Grid>
                      </Grid>

                      <Grid item xs={12} container alignItems='center' justifyContent='flex-end'>
                        {ohioBuildABoxBundle.isLocked && (
                          <Grid item>
                            <Button
                              color='primary'
                              onClick={() => {
                                setIsLayoutLoading(true)
                                generateOhioCoverLetter(ohioBuildABoxBundle.ohioBuildABoxBundleGuid!).finally(() => setIsLayoutLoading(false))
                              }}
                              size='small'
                              variant='contained'
                            >
                              PRINT OHIO COVER LETTER
                            </Button>
                          </Grid>
                        )}

                        <Grid item>
                          <LockButton
                            disabled={values.isLocked}
                            isLocked={ohioBuildABoxBundle.isLocked}
                            onLock={() => {
                              setIsLayoutLoading(true)
                              lockOhioBuildABoxBundle(ohioBuildABoxBundle.ohioBuildABoxBundleGuid!)
                                .then(res => {
                                  enqueueSnackbar('Bundle Locked Successfully!', { variant: 'success' })
                                  setValues({
                                    ...values,
                                    ohioBuildABoxBundles: values.ohioBuildABoxBundles.map(obabb =>
                                      obabb.ohioBuildABoxBundleGuid === ohioBuildABoxBundle.ohioBuildABoxBundleGuid ? res.value : obabb
                                    )
                                  })
                                })
                                .finally(() => setIsLayoutLoading(false))
                            }}
                            onUnlock={() => {
                              setIsLayoutLoading(true)
                              unlockOhioBuildABoxBundle(ohioBuildABoxBundle.ohioBuildABoxBundleGuid!)
                                .then(res => {
                                  enqueueSnackbar('Bundle Unlocked Successfully!', { variant: 'success' })
                                  setValues({
                                    ...values,
                                    ohioBuildABoxBundles: values.ohioBuildABoxBundles.map(obabb =>
                                      obabb.ohioBuildABoxBundleGuid === ohioBuildABoxBundle.ohioBuildABoxBundleGuid ? res.value : obabb
                                    )
                                  })
                                })
                                .finally(() => setIsLayoutLoading(false))
                            }}
                          />
                        </Grid>

                        <Grid item>
                          <DeleteButton
                            disabled={ohioBuildABoxBundle.isLocked}
                            name='Bundle'
                            onDelete={() => {
                              setIsLayoutLoading(true)
                              deleteOhioBuildABoxBundle(ohioBuildABoxBundle.ohioBuildABoxBundleGuid!)
                                .then(() => {
                                  enqueueSnackbar('Bundle Deleted Successfully!', { variant: 'success' })
                                  setValues({
                                    ...values,
                                    ohioBuildABoxBundles: values.ohioBuildABoxBundles.filter(
                                      obabb => obabb.ohioBuildABoxBundleGuid !== ohioBuildABoxBundle.ohioBuildABoxBundleGuid
                                    )
                                  })
                                })
                                .finally(() => setIsLayoutLoading(false))
                            }}
                          />
                        </Grid>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Grid item xs={5} container spacing={1} alignItems='flex-start'>
            <Grid item xs={12}>
              <FedExLabelCard
                deliveryTypes={deliveryTypes}
                onEdit={() => {
                  setFedExInit(values?.fedExOutboundShipment || fedExShipmentInit)
                  setIsFedExDialogOpen('outbound')
                }}
                packageTypes={packageTypes}
                title='FedEx Outbound Label'
                usStateCodes={usStateCodes}
                fedExShipment={values?.fedExOutboundShipment}
                isLocked={values?.isLocked}
              />
            </Grid>

            <Grid item xs={12}>
              <FedExLabelCard
                deliveryTypes={deliveryTypes}
                onEdit={() => {
                  setFedExInit(values?.fedExReturnShipment || fedExShipmentInit)
                  setIsFedExDialogOpen('return')
                }}
                packageTypes={packageTypes}
                title='FedEx Return Label'
                usStateCodes={usStateCodes}
                fedExShipment={values?.fedExReturnShipment}
                isLocked={values?.isLocked}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <GreenText>
            {`**Back Button:**

Clicking this button navigates the user back to the Build A Box List screen.

**Lock/Unlock Button:**

Lock Button: Clicking this button locks the box and switches the button text to "Unlock."

A box can only be locked if:

   - It has at least one bundle.
   - All bundles within the box are locked.

Unlock Button: Clicking this button unlocks the box.

**Delete Button:**

Clicking this button deletes the box.

A box can only be deleted if it is not locked.

**Print Ohio Titles Cover Letter For All Locked Bundles Button:**

Generates a single PDF file with each locked bundle’s Ohio Title Cover Letter on its own page.

This button is only enabled when the box has been locked.

**Print FedEx Labels button:**

Clicking this button generates the FedEx labels for the box.

This button is only enabled when the box has been locked.

**Bundle Locked Chip:**

Displayed when the user has locked the bundle using the Lock Bundle button.

**Vehicles In Bundle:**

Displays the total number of vehicles within the bundle.

**Vehicles In Bundle Edit Button:**

Clicking this button navigates the user to the Build A Box Details: Vehicles in Bundle screen.

**Owner:**

Displays the owner’s name for the bundle.

**Owner Edit Button:**

Clicking this button opens the Owner Add/Edit Dialog.

**Print Ohio Cover Letter Button:**

Clicking this button prints the Ohio Title Cover Letter for the bundle.

This button is only enabled when the bundle has been locked.

**Lock/Unlock Bundle Button:**

Lock Bundle Button: Clicking this button locks the bundle and changes the button text to "Unlock Bundle."

Unlock Bundle Button: Clicking this button unlocks the bundle.

**Delete bundle Button:**

Clicking this button deletes the bundle.

**FedEx Outbound Label Edit Button:**

Clicking this button opens the FedEx Outbound Label Management Dialog.

**Tracking Number:**

Displays the tracking number retrieved from the FedEx API.

**Package Type:**

Displays the package type selected in the FedEx Outbound/Return Label Management Dialog.

**Ship Date:**

Displays the package type selected in the FedEx Outbound/Return Label Management Dialog.

**Ship Time:**

Displays the ship time entered in the FedEx Outbound/Return Label Management Dialog.

**Delivery Type:**

Displays the delivery type entered in the FedEx Outbound/Return Label Management Dialog.

**Weight:**

Displays the package weight entered in the FedEx Outbound/Return Label Management Dialog.

**To:**

Displays the selected title clerk from the FedEx Outbound/Return Label Management Dialog.

**Address:**

Displays the address of the title clerk selected in the FedEx Outbound/Return Label Management Dialog.`}
          </GreenText>
        </Grid>
      </Grid>
    </>
  )
}
